import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from 'appState/store'
import { LoadingBox } from 'components/States'
import { useUserProfile } from 'features/settings/settingsHooks'
import { PathsEnum } from 'pages/Paths'
import {
    getTntIdFromUrl,
    getTntIdFromStorage,
    setTntIdInStorage,
    setTenantIdInStorage,
    hasTntIdInUrl,
    buildUrlWithTntId,
} from 'utils/tenantUrlUtils'
import { httpClaims } from 'utils/http'
import { apiPaths } from 'configs/apiConfigs'
import {
    isUnauthenticatedRoute,
    normalizeOldUrlFormat,
    extractClaimIdFromPath,
} from './utils/routerUtils'
import {
    ITenant,
    initializePreferredTenantInStorage,
    findTenantByTntId,
    handleTenantMismatch,
    handleUrlTenantMismatch,
    handleNoTenantAccess,
} from './utils/tenantRedirectUtils'

interface IRedirectToTenantUrlProps {
    children: React.ReactNode
}

/**
 * Component that handles backward compatibility for old URLs without tenantId
 * and manages tenant-based URL redirects
 */
export const RedirectToTenantUrl = ({ children }: IRedirectToTenantUrlProps) => {
    const location = useLocation()
    const [isReady, setIsReady] = useState(false)
    const processedPathRef = useRef<string | null>(null)
    const hasRenderedChildrenRef = useRef(false)
    const isRedirectingRef = useRef(false)
    const previousTenantCountRef = useRef<number>(-1)
    const redirectAttemptCountRef = useRef<number>(0)
    const lastResetTimestampRef = useRef<number>(0)

    const { user } = useAppSelector((state) => state.auth)
    const { data: profile, isLoading: isProfileLoading } = useUserProfile(user?.id)

    useEffect(() => {
        const pathname = location.pathname
        const currentTenantCount = profile?.tenants?.length ?? 0

        // If we're in the middle of redirecting, don't process anything
        if (isRedirectingRef.current) {
            return
        }

        // Safety check: prevent too many redirects in short time (infinite loop protection)
        const now = Date.now()
        if (redirectAttemptCountRef.current > 5 && (now - lastResetTimestampRef.current) < 3000) {
            hasRenderedChildrenRef.current = true
            setIsReady(true)
            return
        }

        // Detect if tenant list changed (user gained or lost access)
        const tenantListChanged = previousTenantCountRef.current !== -1 &&
            previousTenantCountRef.current !== currentTenantCount

        // If tenant list changed, reset everything to reprocess
        if (tenantListChanged) {
            processedPathRef.current = null
            hasRenderedChildrenRef.current = false
            isRedirectingRef.current = false
            redirectAttemptCountRef.current = 0
            lastResetTimestampRef.current = now
        }

        // Update tenant count tracker
        if (previousTenantCountRef.current !== currentTenantCount) {
            previousTenantCountRef.current = currentTenantCount
        }

        // If already processed this path with current tenant situation, don't run again
        if (processedPathRef.current === pathname && !tenantListChanged) {
            return
        }

        // Reset state when pathname changes
        if (processedPathRef.current !== null && processedPathRef.current !== pathname) {
            hasRenderedChildrenRef.current = false
            redirectAttemptCountRef.current++
            if (redirectAttemptCountRef.current === 1) {
                lastResetTimestampRef.current = now
            }
        }

        // Handle unauthenticated routes immediately
        if (isUnauthenticatedRoute(pathname)) {
            processedPathRef.current = pathname
            setIsReady(true)
            hasRenderedChildrenRef.current = true
            return
        }

        // Wait for profile to load before checking redirects
        if (isProfileLoading || !profile) {
            return
        }

        // Mark this path as processed immediately to prevent re-runs
        processedPathRef.current = pathname

        const checkAndRedirect = async () => {
            if (!profile.tenants || profile.tenants.length === 0) {
                if (pathname !== PathsEnum.home && !pathname.includes('/noaccess-found')) {
                    isRedirectingRef.current = true
                    window.location.href = PathsEnum.home
                    return
                }
                hasRenderedChildrenRef.current = true
                setIsReady(true)
                return
            }

            // Handle URLs with tenant ID
            if (hasTntIdInUrl(pathname)) {
                const shouldRedirect = await handleUrlWithTenant(pathname, profile.tenants)
                if (shouldRedirect) {
                    return // Will redirect, don't set ready
                }
            }
            // Handle old URLs without tenant ID
            else {
                const shouldRedirect = await handleOldUrl(pathname, profile.tenants)
                if (shouldRedirect) {
                    return // Will redirect, don't set ready
                }
            }

            // No redirect needed - safe to render children
            hasRenderedChildrenRef.current = true
            setIsReady(true)
        }

        checkAndRedirect().catch((error) => {
            console.error('[REDIRECT] Error during redirect check:', error)
            hasRenderedChildrenRef.current = true
            setIsReady(true)
        })
    }, [location.pathname, profile, isProfileLoading])

    /**
     * Handles URLs that already have a tenant ID in them
     * Returns true if redirecting, false if safe to render
     */
    const handleUrlWithTenant = async (pathname: string, tenants: ITenant[]): Promise<boolean> => {
        const tntIdFromUrl = getTntIdFromUrl(pathname)
        const tntIdInStorage = getTntIdFromStorage()

        // Case 1: No tenant in storage - initialize with preferred tenant
        if (!tntIdInStorage && tntIdFromUrl) {
            return await handleNoStorageTenant(pathname, tntIdFromUrl, tenants)
        }

        // Case 2: Tenant mismatch between URL and storage
        if (tntIdInStorage && tntIdFromUrl && tntIdInStorage !== tntIdFromUrl) {
            const redirectUrl = handleTenantMismatch(tntIdFromUrl, tntIdInStorage, tenants, pathname)
            if (redirectUrl) {
                isRedirectingRef.current = true
                window.location.href = redirectUrl
                return true
            }
        }

        return false
    }

    /**
     * Handles case where storage has no tenant yet
     * Returns true if redirecting, false if safe to render
     */
    const handleNoStorageTenant = async (
        pathname: string,
        urlTntId: string,
        tenants: ITenant[]
    ): Promise<boolean> => {
        // Initialize storage with preferred tenant
        const preferredTenant = await initializePreferredTenantInStorage(tenants)

        if (!preferredTenant) {
            if (pathname !== PathsEnum.home && !pathname.includes('/noaccess-found')) {
                isRedirectingRef.current = true
                window.location.href = PathsEnum.home
                return true
            }
            return false
        }

        // Check if URL tenant matches preferred tenant
        if (String(preferredTenant.tntId) === urlTntId) {
            return false
        }

        // URL has different tenant - validate and redirect if needed
        const urlTenant = findTenantByTntId(tenants, urlTntId)

        if (urlTenant) {
            const redirectUrl = handleUrlTenantMismatch(urlTenant, preferredTenant, pathname)
            if (redirectUrl) {
                isRedirectingRef.current = true
                window.location.href = redirectUrl
                return true
            }
        } else {
            // User doesn't have access to URL tenant
            const redirectUrl = handleNoTenantAccess(preferredTenant)
            isRedirectingRef.current = true
            window.location.href = redirectUrl
            return true
        }

        return false
    }

    /**
     * Handles old URLs without tenant ID
     * Returns true if redirecting, false if safe to render
     */
    const handleOldUrl = async (pathname: string, tenants: ITenant[]): Promise<boolean> => {
        // Normalize old URL structure
        const normalizedPath = normalizeOldUrlFormat(pathname)

        // For old URLs with claim ID, look up which tenant owns the claim
        if (normalizedPath.includes('/claim/')) {
            const claimId = extractClaimIdFromPath(normalizedPath)

            if (claimId) {
                const redirected = await handleClaimTenantLookup(claimId, normalizedPath, tenants)
                if (redirected) return true
            }
        }

        // Try to get tntId from storage as fallback
        let tntId = getTntIdFromStorage()

        if (!tntId) {
            // No tenant in storage - initialize with preferred tenant
            const preferredTenant = await initializePreferredTenantInStorage(tenants)
            if (preferredTenant) {
                tntId = String(preferredTenant.tntId)
            } else {
                if (pathname !== PathsEnum.home && !pathname.includes('/noaccess-found')) {
                    isRedirectingRef.current = true
                    window.location.href = PathsEnum.home
                    return true
                }
                return false
            }
        }

        if (tntId) {
            const newPath = buildUrlWithTntId(tntId, normalizedPath)
            isRedirectingRef.current = true
            window.location.href = newPath + location.search + location.hash
            return true
        }

        // No tntId available - allow rendering
        return false
    }

    /**
     * Looks up which tenant owns a claim and redirects accordingly
     * Returns true if redirecting, false otherwise
     */
    const handleClaimTenantLookup = async (
        claimId: string,
        normalizedPath: string,
        tenants: ITenant[]
    ): Promise<boolean> => {
        try {
            const response = await httpClaims.get(apiPaths.claimsTenant(claimId))
            const tenantId = response.data

            if (!tenantId) return false

            // Find the tenant to get its tntId
            const matchingTenant = tenants.find(t => t.tenantId === tenantId)

            if (matchingTenant) {
                // Store both IDs and redirect
                setTntIdInStorage(String(matchingTenant.tntId))
                setTenantIdInStorage(tenantId)

                const newPath = buildUrlWithTntId(matchingTenant.tntId, normalizedPath)
                isRedirectingRef.current = true
                window.location.href = newPath + location.search + location.hash
                return true
            } else {
                // User doesn't have access to this tenant
                sessionStorage.setItem(
                    'SELECTED_TENANT_UNAVAILABLE',
                    'The requested Practice is unavailable or you do not have access to it.'
                )
                isRedirectingRef.current = true
                window.location.href = PathsEnum.home
                return true
            }
        } catch (error) {
            console.error('[REDIRECT] Failed to retrieve tenant for claim:', claimId, error)
            return false
        }
    }

    // Once we've rendered children, always keep rendering them (stable mount)
    // Only show loading if we haven't rendered children yet AND not ready
    const shouldShowLoading = !hasRenderedChildrenRef.current && !isReady

    if (shouldShowLoading) {
        return <LoadingBox size={2} />
    }

    return <>{children}</>
}
